/**
 * ThemeToggle - Toggle button za dark/light mode sa Dashboard styling-om
 * 
 * Reusable komponenta koja se koristi kroz celu aplikaciju.
 * Podržava fixed pozicioniranje za navbar.
 * 
 * @example
 * ```tsx
 * <ThemeToggle />
 * <ThemeToggle position="fixed" top="20px" right="20px" />
 * ```
 */

"use client";

import { Box } from "@chakra-ui/react";
import { FiMoon, FiSun } from "react-icons/fi";
import { useDashboardTheme } from "@/components/dashboard/DashboardThemeProvider";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import type { BoxProps } from "@chakra-ui/react";
import { TouchAwareTooltip } from "./TouchAwareTooltip";

export interface ThemeToggleProps extends Omit<BoxProps, "onClick"> {
  /**
   * Size of the button
   */
  size?: "sm" | "md" | "lg";
}

const sizeMap = {
  sm: { w: "32px", h: "32px", iconSize: "16px" },
  md: { w: "40px", h: "40px", iconSize: "20px" },
  lg: { w: "48px", h: "48px", iconSize: "24px" },
};

export const ThemeToggle = ({ size = "sm", ...props }: ThemeToggleProps) => {
  const { mode, toggleMode, canToggleTheme } = useDashboardTheme();
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();

  const isDark = mode === "dark";
  const sizeStyles = sizeMap[size];

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (canToggleTheme) {
      toggleMode();
    }
  };

  // Don't render if theme toggle is disabled
  if (!canToggleTheme) {
    return null;
  }

  return (
    <TouchAwareTooltip
      label={isDark ? "Switch to Light" : "Switch to Dark"}
      placement="bottom"
    >
      <Box
        as="button"
        aria-label={isDark ? "Switch to Light" : "Switch to Dark"}
        onClick={handleClick}
        w={sizeStyles.w}
        h={sizeStyles.h}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderWidth="1px"
        borderColor={theme.borderGlass.default}
        color={theme.text.secondary}
        borderRadius="8px"
        transition="all 0.18s ease-out"
        cursor="pointer"
        sx={{
          "@media (hover: hover)": {
            "&:hover": {
              bg: theme.surfaceGlass.cardStrong,
              borderColor: theme.accent.primaryBorder,
              color: theme.accent.primary,
            },
          },
          "@media (hover: none) and (pointer: coarse)": {
            "&:active": {
              transform: "scale(0.95)",
            },
          },
        }}
        _focus={{
          boxShadow: theme.focusRing,
          outline: "none",
        }}
        {...props}
      >
        {isDark ? (
          <FiSun size={sizeStyles.iconSize} />
        ) : (
          <FiMoon size={sizeStyles.iconSize} />
        )}
      </Box>
    </TouchAwareTooltip>
  );
};

